import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Radio,
  Collapse,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import SearchRounded from "@mui/icons-material/SearchRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ArrowDropDownRounded from "@mui/icons-material/ArrowDropDownRounded";
import ReorderRounded from "@mui/icons-material/ReorderRounded";
import ViewColumnRounded from "@mui/icons-material/ViewColumnRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";

import type { Bot, BrokerAccount } from "@/data/mock-data";
import type {
  Order,
  OrderStatus,
  OrderType
} from "@/data/mock-orders";

// ---- Public types + helpers ------------------------------------------------

export type OrderViewMode = "simple" | "advanced";

/**
 * Date-range presets. Mirrors the range chips on the Portfolio chart
 * conceptually — "90d" here roughly maps to "3M" there.
 *
 * Sergey 2026-04-25: dropped 7d / 30d (review windows shorter than 90d
 * are rare for orders) and added "custom" so users can dial a precise
 * window via the calendar picker.
 */
export type DatePreset = "90d" | "ytd" | "1y" | "all" | "custom";

/**
 * Sentinel "bot id" used inside `OrdersFilterState.botIds` to mean
 * "manual orders" — orders whose `source === "manual"` and which have
 * no `botId` at all. Treating it as a regular entry in the bot list
 * keeps the filter UI uniform (one checkbox per row) instead of
 * splitting into a separate "Source" chip just for this one case.
 */
export const MANUAL_ORDER_BOT_ID = "__manual__";

export interface OrdersFilterState {
  mode: OrderViewMode;
  search: string;
  /**
   * Selected bot ids. Empty array = "all sources" (bot *and* manual).
   * Include `MANUAL_ORDER_BOT_ID` to opt in to manual orders explicitly.
   */
  botIds: string[];
  /**
   * Selected account ids. Empty = "all accounts in the current view".
   * Only meaningful in aggregate (All Live Accounts) mode where orders
   * span multiple accounts; in single-account mode the enclosing panel
   * has already scoped to one account and this array stays empty.
   */
  accountIds: string[];
  /** Empty = all order types. */
  types: OrderType[];
  /** Empty = all statuses. */
  statuses: OrderStatus[];
  dateRange: DatePreset;
  /**
   * Inclusive ISO `YYYY-MM-DD` lower bound. Only consulted when
   * `dateRange === "custom"`. Other presets ignore this field.
   */
  customStart?: string;
  /** Inclusive ISO `YYYY-MM-DD` upper bound. Same gating as customStart. */
  customEnd?: string;
}

export const DEFAULT_ORDERS_FILTER: OrdersFilterState = {
  mode: "simple",
  search: "",
  botIds: [],
  accountIds: [],
  types: [],
  statuses: [],
  // Per Sergey's scoping — 90 days is the default review window. Most
  // orders in the dataset will land inside it without the user having
  // to reach for the filter.
  dateRange: "90d"
};

// ---- Option tables ---------------------------------------------------------

const TYPE_OPTIONS: ReadonlyArray<{ value: OrderType; label: string }> = [
  { value: "market", label: "Market" },
  { value: "limit", label: "Limit" },
  { value: "stop", label: "Stop" },
  { value: "stop_limit", label: "Stop-limit" },
  // Platform-layer types — user/rule-authored orders that WHB holds
  // and releases. Grouped alongside execution types so a single filter
  // covers the full "what kind of order is this" dimension.
  { value: "pending", label: "Pending" },
  { value: "scheduled", label: "Scheduled" },
  { value: "smart", label: "Smart" }
];

const STATUS_OPTIONS: ReadonlyArray<{ value: OrderStatus; label: string }> = [
  { value: "filled", label: "Filled" },
  { value: "partial", label: "Partial" },
  { value: "open", label: "Open" },
  { value: "canceled", label: "Canceled" },
  { value: "expired", label: "Expired" },
  { value: "replaced", label: "Replaced" }
];

const DATE_OPTIONS: ReadonlyArray<{ value: DatePreset; label: string; short: string }> = [
  { value: "90d", label: "Last 90 days", short: "90D" },
  { value: "ytd", label: "Year to date", short: "YTD" },
  { value: "1y", label: "Last 12 months", short: "1Y" },
  { value: "all", label: "All time", short: "All" },
  // "Custom" is rendered specially — selecting it from the menu opens
  // the calendar picker instead of immediately applying. The chip
  // label shifts to the picked range once the user confirms.
  { value: "custom", label: "Custom range…", short: "Custom" }
];

// ---- Pure filter application ----------------------------------------------

interface DateBounds {
  /** Inclusive lower bound in ms-since-epoch, or null for "no lower bound". */
  fromMs: number | null;
  /** Inclusive upper bound in ms-since-epoch, or null for "no upper bound".
   *  Custom ranges set this to end-of-day (23:59:59.999) on customEnd so
   *  same-day fills aren't excluded by clock-time precision. */
  toMs: number | null;
}

/**
 * Resolve a filter's date bounds. Presets define a lower bound only
 * ("last 90 days from now"); custom defines both. "all" clears both.
 */
function resolveDateBounds(filter: OrdersFilterState, now: Date): DateBounds {
  if (filter.dateRange === "all") return { fromMs: null, toMs: null };
  if (filter.dateRange === "custom") {
    const fromMs = filter.customStart
      ? new Date(`${filter.customStart}T00:00:00`).getTime()
      : null;
    const toMs = filter.customEnd
      ? new Date(`${filter.customEnd}T23:59:59.999`).getTime()
      : null;
    return { fromMs, toMs };
  }
  const d = new Date(now);
  switch (filter.dateRange) {
    case "90d":
      d.setDate(d.getDate() - 90);
      return { fromMs: d.getTime(), toMs: null };
    case "1y":
      d.setFullYear(d.getFullYear() - 1);
      return { fromMs: d.getTime(), toMs: null };
    case "ytd":
      return { fromMs: new Date(now.getFullYear(), 0, 1).getTime(), toMs: null };
  }
}

/**
 * Pure function — apply the filter state to an orders list. Consumers
 * lift both the filter state and the source list, then pass the
 * resulting filtered list to whichever view (Simple or Advanced) is
 * currently active. This is intentionally not memoized internally;
 * callers should wrap with `useMemo` keyed on `[orders, filter]`.
 */
export function applyOrdersFilter(
  orders: Order[],
  filter: OrdersFilterState,
  now: Date = new Date()
): Order[] {
  const q = filter.search.trim().toLowerCase();
  const bounds = resolveDateBounds(filter, now);
  return orders.filter((o) => {
    if (q) {
      const hit =
        o.asset.toLowerCase().includes(q) ||
        o.assetName.toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (filter.botIds.length > 0) {
      const match = filter.botIds.some((id) =>
        id === MANUAL_ORDER_BOT_ID ? o.source === "manual" : o.botId === id
      );
      if (!match) return false;
    }
    if (filter.accountIds.length > 0 && !filter.accountIds.includes(o.accountId)) {
      return false;
    }
    if (filter.types.length > 0 && !filter.types.includes(o.orderType)) {
      return false;
    }
    if (filter.statuses.length > 0 && !filter.statuses.includes(o.status)) {
      return false;
    }
    if (bounds.fromMs !== null || bounds.toMs !== null) {
      const t = new Date(o.submittedAt).getTime();
      if (bounds.fromMs !== null && t < bounds.fromMs) return false;
      if (bounds.toMs !== null && t > bounds.toMs) return false;
    }
    return true;
  });
}

// ---- Component -------------------------------------------------------------

interface OrdersFilterBarProps {
  filter: OrdersFilterState;
  onChange: (next: OrdersFilterState) => void;
  /** Bots relevant to the current account (i.e. already scoped). */
  bots: Bot[];
  /**
   * Accounts available for the Account filter chip. Typically the live
   * accounts list when the parent is in aggregate mode. Only rendered
   * when `showAccount` is true.
   */
  accounts?: BrokerAccount[];
  /**
   * Whether to show the Account chip. True in aggregate "All Live
   * Accounts" view; false otherwise (the chip would be redundant when
   * we've already scoped to one account upstream).
   */
  showAccount?: boolean;
  /** Optional extra sx on the outer Box. */
  sx?: SxProps<Theme>;
}

/**
 * Combined filter bar for the Orders tab — owns no filter state of its
 * own. All state lives on the parent (`OrdersPanel` inside
 * `AccountsPageV2`) so both the Simple and Advanced views read from
 * the same filtered list.
 *
 * Layout (top to bottom on mobile — the only form factor this page
 * renders on):
 *  1. Segmented Simple / Advanced toggle, centered.
 *  2. Search input, full width, with a clear (×) button when populated.
 *  3. Horizontal-scroll chip row: Bot, Type, Status, Date range. Each
 *     chip is a filled `Chip` that opens a `Menu` anchored to itself.
 *     Chips tint primary when they carry a non-empty selection so the
 *     user can see at a glance which facets are narrowing the list.
 *
 * The date chip is always considered "active" (it's never empty — one
 * preset is always selected) but we only paint it primary when the
 * user has deviated from the default 90-day window.
 */
export function OrdersFilterBar({
  filter,
  onChange,
  bots,
  accounts,
  showAccount,
  sx
}: OrdersFilterBarProps) {
  const [botAnchor, setBotAnchor] = useState<HTMLElement | null>(null);
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const [typeAnchor, setTypeAnchor] = useState<HTMLElement | null>(null);
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [dateAnchor, setDateAnchor] = useState<HTMLElement | null>(null);

  // Sergey 2026-04-25: chip rail is hidden by default — surfaces only when
  // the user taps the Filter icon next to the search box. This matches
  // Robinhood / Webull mobile patterns where the search +
  // view-toggle live on one always-visible row, and detailed filters are
  // an opt-in disclosure. Initial state is `false` because the default
  // 90-day window is already a narrow-enough scope for most users —
  // surfacing five chips on mount was visual noise.
  // Search is a filter facet too — tapping a ticker inside the order
  // drawer writes the symbol into `search`, and the user expects that
  // to register as an active filter (icon paints primary, chip rail
  // auto-opens via the effect below). Original signal was chip-only,
  // which is why the search-driven filter felt invisible (Sergey
  // 2026-04-25).
  const anyFilterActive =
    filter.search.trim().length > 0 ||
    filter.botIds.length > 0 ||
    filter.accountIds.length > 0 ||
    filter.types.length > 0 ||
    filter.statuses.length > 0 ||
    filter.dateRange !== DEFAULT_ORDERS_FILTER.dateRange;
  // Auto-open the chip row when filters are already active — gives the
  // user an at-a-glance view of which facets are narrowing the list
  // without forcing them to tap the icon to discover what's set.
  const [filtersOpen, setFiltersOpen] = useState<boolean>(anyFilterActive);

  // Whenever a filter becomes active (e.g. user tapped a chip in the
  // order detail drawer that calls onApplyFilter), surface the chip
  // rail so they can see the just-applied facet. Doesn't auto-close
  // when filters clear — closing is the user's explicit action via the
  // Tune icon (see below).
  useEffect(() => {
    if (anyFilterActive && !filtersOpen) setFiltersOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyFilterActive]);

  // Tap on Tune icon — when opening, simply discloses the rail. When
  // closing (the icon is in its activated state), ALSO clears every
  // filter facet AND the search term (Sergey 2026-04-25). The Tune
  // icon thus doubles as a "reset filters" affordance — common pattern
  // in trading-app filter strips (Webull, Schwab).
  function toggleFiltersDisclosure() {
    if (filtersOpen) {
      setFiltersOpen(false);
      // Reset filter + search but keep the user's Simple/Advanced
      // view choice (`mode`) — that's a view preference, not a filter.
      onChange({ ...DEFAULT_ORDERS_FILTER, mode: filter.mode });
    } else {
      setFiltersOpen(true);
    }
  }

  // --- chip labels -------------------------------------------------------

  const botLabel = useMemo(() => {
    if (filter.botIds.length === 0) return "Bot";
    if (filter.botIds.length === 1) {
      const id = filter.botIds[0]!;
      if (id === MANUAL_ORDER_BOT_ID) return "Manual orders";
      const hit = bots.find((b) => b.id === id);
      return hit ? truncate(hit.name, 14) : "Bot";
    }
    return `Bots · ${filter.botIds.length}`;
  }, [filter.botIds, bots]);

  const typeLabel = useMemo(() => {
    if (filter.types.length === 0) return "Type";
    if (filter.types.length === 1) {
      const opt = TYPE_OPTIONS.find((o) => o.value === filter.types[0]);
      return opt ? opt.label : "Type";
    }
    return `Type · ${filter.types.length}`;
  }, [filter.types]);

  const accountLabel = useMemo(() => {
    if (filter.accountIds.length === 0) return "Account";
    if (filter.accountIds.length === 1) {
      const id = filter.accountIds[0]!;
      const hit = accounts?.find((a) => a.id === id);
      return hit ? truncate(hit.name, 14) : "Account";
    }
    return `Accounts · ${filter.accountIds.length}`;
  }, [filter.accountIds, accounts]);

  const statusLabel = useMemo(() => {
    if (filter.statuses.length === 0) return "Status";
    if (filter.statuses.length === 1) {
      const opt = STATUS_OPTIONS.find((o) => o.value === filter.statuses[0]);
      return opt ? opt.label : "Status";
    }
    return `Status · ${filter.statuses.length}`;
  }, [filter.statuses]);

  const dateOpt = DATE_OPTIONS.find((o) => o.value === filter.dateRange);
  // For the chip label: custom shows the picked range ("5/1 – 6/15"),
  // every other preset shows its short token. Falls back to "Date" if
  // the lookup misses (defensive — shouldn't happen with the type
  // guarantee).
  const dateChipLabel =
    filter.dateRange === "custom" && filter.customStart && filter.customEnd
      ? `${formatChipDate(filter.customStart)} – ${formatChipDate(filter.customEnd)}`
      : dateOpt?.short ?? "Date";
  const dateActive = filter.dateRange !== DEFAULT_ORDERS_FILTER.dateRange;
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);

  // --- toggle helpers ----------------------------------------------------

  const toggleBot = (id: string) => {
    const next = filter.botIds.includes(id)
      ? filter.botIds.filter((x) => x !== id)
      : [...filter.botIds, id];
    onChange({ ...filter, botIds: next });
  };

  const toggleAccount = (id: string) => {
    const next = filter.accountIds.includes(id)
      ? filter.accountIds.filter((x) => x !== id)
      : [...filter.accountIds, id];
    onChange({ ...filter, accountIds: next });
  };

  const toggleType = (t: OrderType) => {
    const next = filter.types.includes(t)
      ? filter.types.filter((x) => x !== t)
      : [...filter.types, t];
    onChange({ ...filter, types: next });
  };

  const toggleStatus = (s: OrderStatus) => {
    const next = filter.statuses.includes(s)
      ? filter.statuses.filter((x) => x !== s)
      : [...filter.statuses, s];
    onChange({ ...filter, statuses: next });
  };

  const clearChip = (key: "bot" | "account" | "type" | "status") => {
    if (key === "bot") onChange({ ...filter, botIds: [] });
    if (key === "account") onChange({ ...filter, accountIds: [] });
    if (key === "type") onChange({ ...filter, types: [] });
    if (key === "status") onChange({ ...filter, statuses: [] });
  };

  return (
    <Box sx={sx}>
      {/* Single always-visible row: search + view-toggle + filter. The
       *  Simple/Advanced segmented control and the filter chip rail
       *  used to be separate top-level rows; both have collapsed into
       *  inline icons here per Sergey 2026-04-25. The chip rail is
       *  disclosed below this row via `Collapse` when the user taps
       *  the filter icon. */}
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: filtersOpen ? 1.25 : 0 }}>
        <TextField
          size="small"
          placeholder="Search ticker or name"
          value={filter.search}
          onChange={(e) => onChange({ ...filter, search: e.target.value })}
          // Focusing the search bar auto-discloses the chip rail —
          // search and filter facets are related actions, and surfacing
          // them together when the user is in "narrow the list" mode
          // saves an extra tap. Stays open after blur; the user
          // explicitly closes via the Tune icon when done.
          onFocus={() => {
            if (!filtersOpen) setFiltersOpen(true);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: filter.search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => onChange({ ...filter, search: "" })}
                  sx={{ p: 0.25 }}
                >
                  <CloseRounded sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
            sx: {
              borderRadius: 2,
              bgcolor: (t) => t.palette.action.hover,
              "& fieldset": { border: "none" },
              fontSize: "1rem",
              height: 44
            }
          }}
          // Inside a horizontal Stack alongside the view-toggle and
          // filter icons, `fullWidth` would force 100% width and push
          // the icons offscreen. `flex: 1` + `minWidth: 0` lets it
          // grow to fill remaining space while honoring the icons'
          // intrinsic widths.
          sx={{ flex: 1, minWidth: 0 }}
        />

        {/* View-toggle: Simple (cards) and Advanced (table). Two separate
         *  IconButtons rather than a ToggleButtonGroup because the row's
         *  visual weight has to match a single search field height — a
         *  dual-segment pill would push the search down a row on narrow
         *  viewports. The active variant gets primary color so the
         *  selected mode is obvious at a glance. */}
        <Tooltip title="Simple view">
          <IconButton
            size="small"
            aria-label="Simple view"
            onClick={() => {
              if (filter.mode !== "simple") onChange({ ...filter, mode: "simple" });
            }}
            sx={{
              color: filter.mode === "simple" ? "primary.main" : "text.secondary",
              bgcolor: (t) =>
                filter.mode === "simple" ? t.palette.action.selected : "transparent"
            }}
          >
            <ReorderRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Advanced (table) view">
          <IconButton
            size="small"
            aria-label="Advanced view"
            onClick={() => {
              if (filter.mode !== "advanced") onChange({ ...filter, mode: "advanced" });
            }}
            sx={{
              color: filter.mode === "advanced" ? "primary.main" : "text.secondary",
              bgcolor: (t) =>
                filter.mode === "advanced" ? t.palette.action.selected : "transparent"
            }}
          >
            <ViewColumnRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>

        {/* Filter disclosure. Opens / closes the chip rail below. The
         *  icon paints primary when filters are active OR the rail is
         *  open, so the user always has a visual signal that something
         *  beyond the default 90-day window is in effect. */}
        <Tooltip title={filtersOpen ? "Hide filters" : "Show filters"}>
          <IconButton
            size="small"
            aria-label={filtersOpen ? "Hide and clear filters" : "Show filters"}
            onClick={toggleFiltersDisclosure}
            sx={{
              color:
                filtersOpen || anyFilterActive ? "primary.main" : "text.secondary",
              bgcolor: (t) =>
                filtersOpen ? t.palette.action.selected : "transparent"
            }}
          >
            <TuneRounded sx={{ fontSize: 22 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Chip rail — disclosed via Collapse. `overflowX: auto` makes it
       *  horizontally scrollable when chips overflow; the scrollbar is
       *  hidden visually to match the V2 range row on the chart. */}
      <Collapse in={filtersOpen} timeout={200} unmountOnExit>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: "auto",
            pb: 0.5,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none"
          }}
        >
        {/* Date chip moved to first position (Sergey 2026-04-25) — most
         *  users reach for date narrowing first when reviewing orders;
         *  putting it leftmost saves a horizontal scroll on narrow
         *  viewports. Reset-on-clear returns the chip to the default
         *  90-day window and drops the custom range if any. */}
        <FilterChip
          label={dateChipLabel}
          active={dateActive}
          onClick={(e) => setDateAnchor(e.currentTarget)}
          onClear={
            dateActive
              ? () =>
                  onChange({
                    ...filter,
                    dateRange: DEFAULT_ORDERS_FILTER.dateRange,
                    customStart: undefined,
                    customEnd: undefined
                  })
              : undefined
          }
        />
        <FilterChip
          label={botLabel}
          active={filter.botIds.length > 0}
          onClick={(e) => setBotAnchor(e.currentTarget)}
          onClear={
            filter.botIds.length > 0 ? () => clearChip("bot") : undefined
          }
        />
        {showAccount && (
          <FilterChip
            label={accountLabel}
            active={filter.accountIds.length > 0}
            onClick={(e) => setAccountAnchor(e.currentTarget)}
            onClear={
              filter.accountIds.length > 0 ? () => clearChip("account") : undefined
            }
          />
        )}
        <FilterChip
          label={typeLabel}
          active={filter.types.length > 0}
          onClick={(e) => setTypeAnchor(e.currentTarget)}
          onClear={
            filter.types.length > 0 ? () => clearChip("type") : undefined
          }
        />
        <FilterChip
          label={statusLabel}
          active={filter.statuses.length > 0}
          onClick={(e) => setStatusAnchor(e.currentTarget)}
          onClear={
            filter.statuses.length > 0 ? () => clearChip("status") : undefined
          }
        />
        </Stack>
      </Collapse>

      {/* ---- Menus ---- */}

      {/* Bot menu: multi-select + "Manual orders" as a virtual entry at
       *  the top. Showing manual separately from the bot list keeps it
       *  findable — otherwise manual orders are invisible in a UI that
       *  only talks about bots. */}
      <Menu
        anchorEl={botAnchor}
        open={Boolean(botAnchor)}
        onClose={() => setBotAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <MenuItem
          onClick={() => toggleBot(MANUAL_ORDER_BOT_ID)}
          dense
        >
          <Checkbox
            edge="start"
            size="small"
            checked={filter.botIds.includes(MANUAL_ORDER_BOT_ID)}
            tabIndex={-1}
            disableRipple
          />
          <ListItemText primary="Manual orders" />
        </MenuItem>
        {bots.length > 0 && <Divider sx={{ my: 0.5 }} />}
        {bots.map((b) => (
          <MenuItem key={b.id} onClick={() => toggleBot(b.id)} dense>
            <Checkbox
              edge="start"
              size="small"
              checked={filter.botIds.includes(b.id)}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={b.name} />
          </MenuItem>
        ))}
        {bots.length === 0 && (
          <MenuItem disabled dense>
            <ListItemText
              primary="No bots on this account"
              primaryTypographyProps={{ color: "text.secondary" }}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Account menu — multi-select. Only mounted when `showAccount`
       *  is true (aggregate mode); single-account views never render
       *  the anchor chip so this menu is unreachable but still costs
       *  nothing to keep hanging in the tree. */}
      <Menu
        anchorEl={accountAnchor}
        open={Boolean(accountAnchor)}
        onClose={() => setAccountAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {(accounts ?? []).map((a) => (
          <MenuItem key={a.id} onClick={() => toggleAccount(a.id)} dense>
            <Checkbox
              edge="start"
              size="small"
              checked={filter.accountIds.includes(a.id)}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText
              primary={a.name}
              secondary={a.provider}
              primaryTypographyProps={{ variant: "body2" }}
              secondaryTypographyProps={{
                variant: "caption",
                color: "text.secondary"
              }}
            />
          </MenuItem>
        ))}
        {(!accounts || accounts.length === 0) && (
          <MenuItem disabled dense>
            <ListItemText
              primary="No accounts available"
              primaryTypographyProps={{ color: "text.secondary" }}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Type menu — multi-select */}
      <Menu
        anchorEl={typeAnchor}
        open={Boolean(typeAnchor)}
        onClose={() => setTypeAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        {TYPE_OPTIONS.map((o) => (
          <MenuItem key={o.value} onClick={() => toggleType(o.value)} dense>
            <Checkbox
              edge="start"
              size="small"
              checked={filter.types.includes(o.value)}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Status menu — multi-select */}
      <Menu
        anchorEl={statusAnchor}
        open={Boolean(statusAnchor)}
        onClose={() => setStatusAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        {STATUS_OPTIONS.map((o) => (
          <MenuItem key={o.value} onClick={() => toggleStatus(o.value)} dense>
            <Checkbox
              edge="start"
              size="small"
              checked={filter.statuses.includes(o.value)}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
      </Menu>

      {/* Date menu — single-select radio group. Picking "Custom" opens
       *  the calendar dialog instead of immediately applying; the
       *  dialog's Apply commits both the dateRange flag and the
       *  customStart/customEnd dates. */}
      <Menu
        anchorEl={dateAnchor}
        open={Boolean(dateAnchor)}
        onClose={() => setDateAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {DATE_OPTIONS.map((o) => (
          <MenuItem
            key={o.value}
            dense
            onClick={() => {
              if (o.value === "custom") {
                setDateAnchor(null);
                setDateRangeDialogOpen(true);
                return;
              }
              onChange({
                ...filter,
                dateRange: o.value,
                customStart: undefined,
                customEnd: undefined
              });
              setDateAnchor(null);
            }}
          >
            <Radio
              edge="start"
              size="small"
              checked={filter.dateRange === o.value}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
      </Menu>

      <CustomDateRangeDialog
        open={dateRangeDialogOpen}
        onClose={() => setDateRangeDialogOpen(false)}
        initialFrom={filter.customStart ?? defaultCustomFrom()}
        initialTo={filter.customEnd ?? todayIso()}
        onApply={(from, to) => {
          onChange({
            ...filter,
            dateRange: "custom",
            customStart: from,
            customEnd: to
          });
          setDateRangeDialogOpen(false);
        }}
      />
    </Box>
  );
}

// ---- Internal bits --------------------------------------------------------

/**
 * Filter chip — rounded pill with a leading label, a trailing
 * dropdown caret, and (when the filter carries a selection) a small
 * × affordance. Clicking the body opens the menu; clicking the ×
 * clears the filter without opening anything.
 *
 * We intentionally use MUI's `Chip` rather than a plain Button — it
 * gives us the dense rounded look and the built-in `onDelete` slot
 * we use for the inline clear action.
 */
function FilterChip({
  label,
  active,
  onClick,
  onClear
}: {
  label: string;
  active: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  onClear?: () => void;
}) {
  return (
    <Chip
      label={
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          sx={{ minWidth: 0 }}
        >
          <Box
            component="span"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {label}
          </Box>
          {!onClear && (
            <ArrowDropDownRounded sx={{ fontSize: 18, ml: 0.25 }} />
          )}
        </Stack>
      }
      clickable
      onClick={onClick}
      onDelete={onClear}
      deleteIcon={<CloseRounded sx={{ fontSize: 16 }} />}
      size="small"
      sx={{
        flexShrink: 0,
        borderRadius: 999,
        px: 0.5,
        fontWeight: 500,
        // Apr-2026 typography sweep: body1 (1rem) + proportional
        // height bump — filter chips were markedly smaller than the
        // Simple/Advanced toggle and "Buying Power" row, which made
        // the filter rail read as a secondary control.
        fontSize: "1rem",
        height: 36,
        bgcolor: (t) =>
          active
            ? t.palette.mode === "dark"
              ? "rgba(144,202,249,0.18)"
              : "rgba(25,118,210,0.12)"
            : t.palette.action.hover,
        color: active ? "primary.main" : "text.primary",
        "&:hover": {
          bgcolor: (t) =>
            active
              ? t.palette.mode === "dark"
                ? "rgba(144,202,249,0.26)"
                : "rgba(25,118,210,0.20)"
              : t.palette.action.selected
        },
        "& .MuiChip-deleteIcon": {
          color: active ? "primary.main" : "text.secondary",
          ml: 0.25,
          "&:hover": { color: "primary.main" }
        }
      }}
    />
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// ---- Date helpers ---------------------------------------------------------

/** Today's date as `YYYY-MM-DD` in the runner's local zone — what the
 *  HTML5 `<input type="date">` consumes. */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Default "From" when the user opens the picker for the first time —
 *  90 days back, matching the chip's default preset. */
function defaultCustomFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Compact M/D label for the chip face when a custom range is set —
 *  "5/1", "12/31". Year omitted to keep the chip narrow; full dates
 *  stay visible inside the picker dialog. */
function formatChipDate(iso: string): string {
  // ISO is `YYYY-MM-DD`; slice rather than parse-via-Date so we don't
  // get bitten by tz interpretation (Date treats no-time ISO as UTC).
  if (!iso || iso.length < 10) return iso;
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  return `${m}/${d}`;
}

// ---- Custom date-range dialog ---------------------------------------------

/**
 * Two-`<input type="date">` dialog with Apply / Cancel. Mirrors the
 * picker on the chart's range row so the UX feels consistent across
 * the page. Validates `from <= to` before enabling Apply.
 */
function CustomDateRangeDialog({
  open,
  onClose,
  onApply,
  initialFrom,
  initialTo
}: {
  open: boolean;
  onClose: () => void;
  onApply: (from: string, to: string) => void;
  initialFrom: string;
  initialTo: string;
}) {
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);

  // Re-sync each time the dialog opens so it reflects the currently
  // applied range, not stale edits from a prior cancelled session.
  useEffect(() => {
    if (open) {
      setFrom(initialFrom);
      setTo(initialTo);
    }
  }, [open, initialFrom, initialTo]);

  const valid = !!from && !!to && from <= to;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Custom date range</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            type="date"
            label="From"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          <TextField
            type="date"
            label="To"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            fullWidth
          />
          {!valid && from && to && (
            <Typography variant="caption" color="error">
              From must be on or before To.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid}
          onClick={() => onApply(from, to)}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
